import { createContext, useContext, useMemo, useState, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { setupService, SetupState } from '@/services/setupService';
import { SETUP_MODULES_BY_KEY, SetupModuleKey } from '@/lib/setupModules';
import { scrollAppToTop } from '@/lib/scrollAppToTop';
import { useModuleStops } from '@/hooks/useModuleStops';

interface WizardRun {
  order: SetupModuleKey[];
  index: number;    // which section
  subIndex: number; // which page (stop) within the section
  // 'wizard'  — the admin's full linear run; finishing resolves the module.
  // 'section' — a single section's scoped tour, run by the leader it was handed
  //             to. It must NOT try to resolve the module: that write is
  //             admin-only, and an assignee finishing a tour hasn't finished the
  //             section's work, only the walkthrough of it.
  mode?: 'wizard' | 'section';
  sectionId?: string; // set on 'section' runs — where to return afterwards
}

interface SetupContextValue {
  setupState: SetupState | undefined;
  isSetupActive: boolean;
  run: WizardRun | null;
  currentModule: SetupModuleKey | null;
  startRun: (order: SetupModuleKey[]) => void;
  /** Tour one section's pages, then hand the user to that section's checklist. */
  startSectionTour: (sectionId: string, moduleKey: SetupModuleKey) => void;
  endSectionTour: () => void;
  nextStop: () => void;    // advance to the next page within the section
  prevStop: () => void;    // go back one page, or to the previous section's last page
  canGoBack: boolean;      // false only on the very first page of the run
  advanceRun: () => void;  // finish the section, move to the next one
  exitRun: () => void;
  refetchSetup: () => void;
}

const RUN_STORAGE_KEY = 'ldg-setup-run';

const SetupContext = createContext<SetupContextValue | null>(null);

export function SetupProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isStoreAdmin = Boolean(user && (user.isAdmin || user.role === 'admin'));
  // Tour pages per module, minus pages the store switched off in Settings
  const moduleStops = useModuleStops();

  const { data: setupState, refetch } = useQuery({
    queryKey: ['store-setup'],
    queryFn: setupService.getSetup,
    enabled: isStoreAdmin,
    staleTime: 30_000
  });

  const [run, setRun] = useState<WizardRun | null>(() => {
    try {
      const raw = sessionStorage.getItem(RUN_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Validate the stored shape — a stale run from an older deploy (e.g. a
      // renamed module key) must not crash the app shell via SetupBar.
      if (!Array.isArray(parsed?.order) || typeof parsed?.index !== 'number') return null;
      if (!parsed.order.every((k: string) => k in SETUP_MODULES_BY_KEY)) return null;
      if (parsed.index < 0 || parsed.index >= parsed.order.length) return null;
      const subIndex = typeof parsed.subIndex === 'number' ? parsed.subIndex : 0;
      const stops = SETUP_MODULES_BY_KEY[parsed.order[parsed.index] as SetupModuleKey]?.stops ?? [];
      if (subIndex < 0 || (stops.length > 0 && subIndex >= stops.length)) return null;
      return {
        order: parsed.order, index: parsed.index, subIndex,
        mode: parsed.mode === 'section' ? 'section' : 'wizard',
        sectionId: typeof parsed.sectionId === 'string' ? parsed.sectionId : undefined
      };
    } catch { return null; }
  });

  const persistRun = (next: WizardRun | null) => {
    setRun(next);
    if (next) sessionStorage.setItem(RUN_STORAGE_KEY, JSON.stringify(next));
    else sessionStorage.removeItem(RUN_STORAGE_KEY);
  };

  const stopRoute = (key: SetupModuleKey, subIndex: number): string | null => {
    const stops = moduleStops(key);
    return stops[subIndex]?.route ?? stops[0]?.route ?? null;
  };

  const goTo = (key: SetupModuleKey, subIndex: number) => {
    const route = stopRoute(key, subIndex);
    if (route) navigate(route);
    scrollAppToTop();
  };

  const startRun = (order: SetupModuleKey[]) => {
    if (!order.length) return;
    persistRun({ order, index: 0, subIndex: 0, mode: 'wizard' });
    goTo(order[0], 0);
  };

  const startSectionTour = (sectionId: string, moduleKey: SetupModuleKey) => {
    if (!moduleStops(moduleKey).length) {
      // Nothing to tour (a section with no module, e.g. Assessments) — send them
      // straight to the work rather than showing an empty walkthrough.
      navigate(`/setup?section=${sectionId}`);
      return;
    }
    persistRun({ order: [moduleKey], index: 0, subIndex: 0, mode: 'section', sectionId });
    goTo(moduleKey, 0);
  };

  const endSectionTour = () => {
    const sectionId = run?.sectionId;
    persistRun(null);
    navigate(sectionId ? `/setup?section=${sectionId}` : '/setup');
    scrollAppToTop();
  };

  const nextStop = () => {
    if (!run) return;
    const key = run.order[run.index];
    const stops = moduleStops(key);
    const nextSub = run.subIndex + 1;
    if (nextSub >= stops.length) return; // section finished — that's advanceRun's job
    persistRun({ ...run, subIndex: nextSub });
    goTo(key, nextSub);
  };

  const prevStop = () => {
    if (!run) return;
    if (run.subIndex > 0) {
      const sub = run.subIndex - 1;
      persistRun({ ...run, subIndex: sub });
      goTo(run.order[run.index], sub);
      return;
    }
    // On the first page of a section — step back to the previous section's last page.
    if (run.index > 0) {
      const prevKey = run.order[run.index - 1];
      const lastSub = Math.max(0, (moduleStops(prevKey).length || 1) - 1);
      persistRun({ order: run.order, index: run.index - 1, subIndex: lastSub });
      goTo(prevKey, lastSub);
    }
  };

  const advanceRun = () => {
    if (!run) return;
    queryClient.invalidateQueries({ queryKey: ['store-setup'] });
    const nextIndex = run.index + 1;
    if (nextIndex >= run.order.length) {
      setupService.trackEvent('setup_completed');
      persistRun(null);
      navigate('/getting-started?finished=1');
      return;
    }
    persistRun({ order: run.order, index: nextIndex, subIndex: 0 });
    goTo(run.order[nextIndex], 0);
  };

  const exitRun = () => {
    // An assignee has no business on the admin's Getting Started hub — bailing
    // out of a scoped tour drops them on their own checklist instead.
    const sectionId = run?.mode === 'section' ? run.sectionId : null;
    persistRun(null);
    navigate(sectionId ? `/setup?section=${sectionId}` : '/getting-started');
  };

  const value = useMemo<SetupContextValue>(() => ({
    setupState,
    isSetupActive: isStoreAdmin && setupState?.status === 'in_progress',
    run,
    currentModule: run ? run.order[run.index] : null,
    canGoBack: !!run && (run.subIndex > 0 || run.index > 0),
    startRun, startSectionTour, endSectionTour, nextStop, prevStop, advanceRun, exitRun,
    refetchSetup: refetch
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [setupState, isStoreAdmin, run]);

  return <SetupContext.Provider value={value}>{children}</SetupContext.Provider>;
}

export function useSetup() {
  const ctx = useContext(SetupContext);
  if (!ctx) throw new Error('useSetup must be used within SetupProvider');
  return ctx;
}
